import { InventoryItem } from '@/types/inventory';
import { supabase } from '@/integrations/supabase/client';

/**
 * Optimized inventory search service
 * Phase 1: Simplified search logic using only database function
 * Phase 3: Simple relevance scoring and result filtering
 * Phase 4: Data consistency improvements
 */

interface SearchResult {
  product_naam: string;
  artikelnummer: string;
  locatie: string;
  voorraad: number;
  scancode: string;
  eenheid: string;
  magazijn: number;
  moq: number;
  similarity: number;
}

// Helper to safely extract value from JSONB field
const extractValue = (data: any, defaultValue: any = ''): any => {
  if (!data) return defaultValue;
  
  if (typeof data === 'object' && data !== null) {
    // Handle JSONB objects - get first value or handle arrays
    if (Array.isArray(data)) {
      return data[0] || defaultValue;
    }
    const firstValue = Object.values(data)[0];
    return firstValue !== undefined ? firstValue : defaultValue;
  }
  
  return data || defaultValue;
};

// Extract number from value that might be string or number
const extractNumber = (value: any): number => {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  
  const strValue = String(value).trim();
  const matches = strValue.match(/[0-9]*[.,]?[0-9]+/);
  if (matches && matches[0]) {
    return parseFloat(matches[0].replace(',', '.'));
  }
  
  return 0;
};

// Standardize warehouse field to number
const extractWarehouse = (magazijn: any): number => {
  if (typeof magazijn === 'number') return magazijn;
  if (!magazijn) return 0;
  
  const parsed = parseInt(String(magazijn).trim(), 10);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Main optimized search function
 * Uses database function with improved result processing
 */
export const searchInventoryOptimized = async (
  searchTerm: string,
  warehouseId: number | null = null
): Promise<{ results: InventoryItem[]; lastUpdate: string | null }> => {
  // Input validation and normalization
  const normalizedTerm = searchTerm.trim();
  
  // Minimum length validation (matches database function)
  if (normalizedTerm.length < 2) {
    console.log('🔍 Search term too short, returning empty results');
    return { results: [], lastUpdate: null };
  }

  console.log(`🔍 Enhanced search for: "${normalizedTerm}"`);
  
  try {
    // Use optimized database function
    const { data, error } = await supabase.rpc('search_voorraad_goes', {
      search_term: normalizedTerm
    });

    if (error) {
      console.error('Database search error:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      console.log('No results found in database');
      return { results: [], lastUpdate: null };
    }

    console.log(`📊 Database returned ${data.length} results`);

    // Process and standardize results
    const processedResults: InventoryItem[] = data.map((item: any) => {
      const voorraad = extractNumber(extractValue(item.voorraad, 0));
      const magazijn = extractWarehouse(extractValue(item.magazijn, 0));
      const moq = extractNumber(extractValue(item.moq, 0));
      
      return {
        product_naam: extractValue(item.product_naam, ''),
        product_id: extractValue(item.artikelnummer, ''),
        bc: extractValue(item.Scancode, ''),
        locatie: extractValue(item.locatie, ''),
        voorraad,
        eenheid: extractValue(item.Eenheid, ''),
        magazijn,
        moq,
        timestamp_sync: new Date().toISOString(),
        id: `${extractValue(item.artikelnummer, '')}-${magazijn}`,
        similarity: item.similarity || 0
      };
    });

    // Filter by warehouse if specified
    let filteredResults = processedResults;
    if (warehouseId !== null) {
      filteredResults = processedResults.filter(item => item.magazijn === warehouseId);
      console.log(`🏢 Filtered to warehouse ${warehouseId}: ${filteredResults.length} results`);
    }

    // Apply enhanced relevance filtering
    const limitedResults = applyEnhancedRelevanceFiltering(filteredResults, normalizedTerm);
    
    console.log(`✨ Final enhanced results: ${limitedResults.length} items`);
    
    return {
      results: limitedResults,
      lastUpdate: new Date().toISOString()
    };

  } catch (error) {
    console.error('Enhanced search error:', error);
    throw error;
  }
};

/**
 * Enhanced relevance filtering with improved scoring and prioritization
 */
const applyEnhancedRelevanceFiltering = (results: InventoryItem[], searchTerm: string): InventoryItem[] => {
  if (results.length === 0) return results;
  
  const searchLower = searchTerm.toLowerCase();
  const searchLength = searchTerm.length;
  
  // Calculate enhanced relevance score for each item
  const scoredResults = results.map(item => {
    const productName = item.product_naam.toLowerCase();
    const productId = item.product_id.toLowerCase();
    const barcode = item.bc.toLowerCase();
    
    let score = item.similarity || 0;
    let matchType = 'similarity';
    
    // Exact match scoring (highest priority)
    if (productName === searchLower || productId === searchLower || barcode === searchLower) {
      score = 1000;
      matchType = 'exact';
    }
    // Starts with match scoring (very high priority)
    else if (productName.startsWith(searchLower) || productId.startsWith(searchLower) || barcode.startsWith(searchLower)) {
      score = 900;
      matchType = 'starts_with';
    }
    // Contains match scoring with position weighting
    else if (productName.includes(searchLower) || productId.includes(searchLower) || barcode.includes(searchLower)) {
      // Better score for matches closer to beginning
      const nameIndex = productName.indexOf(searchLower);
      const idIndex = productId.indexOf(searchLower);
      const bcIndex = barcode.indexOf(searchLower);
      
      const bestIndex = Math.min(
        nameIndex >= 0 ? nameIndex : 999,
        idIndex >= 0 ? idIndex : 999,
        bcIndex >= 0 ? bcIndex : 999
      );
      
      score = 800 - (bestIndex * 10); // Closer to start = higher score
      matchType = 'contains';
    }
    
    // Boost score based on stock availability
    if (item.voorraad && item.voorraad > 0) {
      score += Math.min(item.voorraad * 0.1, 50); // Max 50 point boost
    }
    
    // Boost score for products with location (better availability)
    if (item.locatie && item.locatie.trim() !== '') {
      score += 25;
    }
    
    return {
      ...item,
      enhanced_score: score,
      match_type: matchType
    };
  });
  
  // Sort by enhanced score (descending)
  scoredResults.sort((a, b) => b.enhanced_score - a.enhanced_score);
  
  // Intelligent limiting based on search term length and result quality
  let maxResults = 50; // Default limit
  
  if (searchLength === 2) {
    maxResults = 30; // More restrictive for short terms
  } else if (searchLength >= 5) {
    maxResults = 100; // More results for longer, specific terms
  }
  
  // Filter out very low relevance results
  const minScore = searchLength >= 4 ? 100 : 200;
  const filteredResults = scoredResults.filter(item => item.enhanced_score >= minScore);
  
  const finalResults = filteredResults.slice(0, maxResults);
  
  console.log(`📈 Enhanced filtering: ${finalResults.length} results (min score: ${minScore}, max: ${maxResults})`);
  
  return finalResults;
};

/**
 * Get available warehouses from current data
 */
export const getAvailableWarehouses = async (): Promise<{ id: number; name: string }[]> => {
  try {
    const { data, error } = await supabase
      .from('voorraad_live2')
      .select('magazijn')
      .not('magazijn', 'is', null);

    if (error) throw error;

    // Extract unique warehouse IDs and sort
    const uniqueWarehouses = [...new Set(data.map(item => extractWarehouse(item.magazijn)))]
      .filter(id => id > 0)
      .sort((a, b) => a - b);

    return uniqueWarehouses.map(id => ({
      id,
      name: `Magazijn ${id}`
    }));

  } catch (error) {
    console.error('Error fetching warehouses:', error);
    return [];
  }
};